import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

/** The public site runs the dark theme; the dashboard runs the light one. The
 *  wrapper cannot paint the overscroll area above and below the page, so the
 *  body gets a class for as long as a public route is mounted and loses it on
 *  the way out -- otherwise the dashboard would inherit an ink background. */
function useDarkBody() {
  useEffect(() => {
    document.body.classList.add('pub-body');
    return () => document.body.classList.remove('pub-body');
  }, []);
}

function Logo() {
  return (
    <Link className="logo" to="/">
      {/* Decorative: the wordmark beside it already carries the name, so the
          glyph is not announced twice. */}
      <span className="logo-mark" aria-hidden="true" />
      mailgiri
    </Link>
  );
}

/** The marketing footer: copyright on the left, links on the right. */
function Footer() {
  return (
    <footer>
      <div>&copy; 2026 mailgiri</div>
      <div className="footer-links">
        <a href="/docs" target="_blank" rel="noreferrer">
          Docs
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
        <Link to="/pricing">Pricing</Link>
        <Link to="/login">Log in</Link>
        <Link to="/signup">Sign up</Link>
      </div>
    </footer>
  );
}

/** The auth footer, which the design deliberately makes different: centred, one
 *  line, no links. Somebody part-way through signing in does not need a nav. */
function AuthFooter() {
  return <footer className="auth-footer">&copy; 2026 mailgiri</footer>;
}

/** Marketing pages: full nav, footer inside the same width as the content. */
export function PublicShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  useDarkBody();

  return (
    <div className="pub">
      <div className="wrap">
        <header>
          <div className="nav-row">
            <Logo />
            <nav className="links">
              {/* aria-current tells a screen reader which nav item is the page
                  you are on -- colour alone would not say it. */}
              {[
                // Docs opens in its own tab, here as in the dashboard sidebar.
                // Somebody reading how to send their first email is copying
                // values out of another page while they do it, and taking the
                // tab they were reading from is the thing that makes docs
                // irritating to use.
                { to: '/docs', label: 'Docs', newTab: true },
                { to: '/pricing', label: 'Pricing', newTab: false },
              ].map((item) =>
                item.newTab ? (
                  <a
                    key={item.to}
                    className="navlink"
                    href={item.to}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {item.label}
                    {/* Announced: "opens in a new tab" is something a sighted
                        reader infers from the behaviour and nobody else does. */}
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                ) : (
                <Link
                  key={item.to}
                  className={pathname === item.to ? 'navlink current' : 'navlink'}
                  to={item.to}
                  aria-current={pathname === item.to ? 'page' : undefined}
                >
                  {item.label}
                </Link>
                ),
              )}
              <Link className="btn-login" to="/login">
                Log in
              </Link>
              <Link className="btn-signup" to="/signup">
                Sign up
              </Link>
            </nav>
          </div>
        </header>
      </div>

      <main className="wrap">{children}</main>

      <div className="wrap">
        <Footer />
      </div>
    </div>
  );
}

/** Auth pages: a pared-back header carrying only the cross-link to the other
 *  form, and the card centred in whatever height is left. */
export function AuthShell({
  crossLink,
  children,
}: {
  crossLink: { to: string; label: string };
  children: React.ReactNode;
}) {
  useDarkBody();

  return (
    <div className="pub">
      <div className="wrap">
        <header>
          <div className="nav-row">
            <Logo />
            <Link className="navlink" to={crossLink.to}>
              {crossLink.label}
            </Link>
          </div>
        </header>
      </div>

      <main className="auth-shell">
        <div className="auth-card">{children}</div>
      </main>

      <div className="wrap">
        <AuthFooter />
      </div>
    </div>
  );
}
